/**
 * /accounting/gl — General Ledger hub.
 *
 * Entry tile for Chart of Accounts, Journal Entries, Trial Balance,
 * and GL Detail. Mirrors the cockpit's flat-card language.
 */
import { Link } from "react-router-dom";
import { BookOpen, FileText, Scale, ListTree, Banknote, Receipt, TrendingUp } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useSeedGeneralLedger } from "@/data/accounting/useSeedGeneralLedger";

import { BooksNavRail } from "@/components/accounting/BooksNavRail";
const fmt = (n: number) =>
  `$${Math.round(n).toLocaleString()}`;

export default function GeneralLedgerHubPage() {
  useSeedGeneralLedger();
  const { accounts, journalEntries, getTrialBalance } = useGeneralLedger();

  const tb = getTrialBalance();
  const totalDebits = tb.reduce((s, b) => s + b.debit, 0);
  const totalCredits = tb.reduce((s, b) => s + b.credit, 0);
  const inBalance = Math.round(totalDebits * 100) === Math.round(totalCredits * 100);

  const tiles: Array<{
    to: string; icon: typeof BookOpen; title: string; sub: string; metric: string;
  }> = [
    {
      to: "/accounting/gl/chart-of-accounts",
      icon: ListTree,
      title: "Chart of Accounts",
      sub: "Account structure, types, subledgers",
      metric: `${accounts.filter(a => a.active).length} accounts`,
    },
    {
      to: "/accounting/gl/journal-entries",
      icon: FileText,
      title: "Journal Entries",
      sub: "Manual & auto-posted; append-only",
      metric: `${journalEntries.filter(j => j.status === "posted").length} posted`,
    },
    {
      to: "/accounting/gl/trial-balance",
      icon: Scale,
      title: "Trial Balance",
      sub: "All accounts; DR vs CR by period",
      metric: inBalance ? "In balance" : "Out of balance",
    },
    {
      to: "/accounting/gl/detail",
      icon: BookOpen,
      title: "GL Detail",
      sub: "Drill-down transactions by account",
      metric: `${journalEntries.reduce((s, j) => s + j.lines.length, 0)} lines`,
    },
    {
      to: "/accounting/bank-rec",
      icon: Banknote,
      title: "Bank & CC Reconciliation",
      sub: "Match statement lines · clear Unreconciled Cash",
      metric: "Open hub",
    },
    {
      to: "/accounting/ap",
      icon: Receipt,
      title: "Accounts Payable",
      sub: "Vendor bills · payments · statement rec",
      metric: "Open hub",
    },
    {
      to: "/accounting/reports",
      icon: TrendingUp,
      title: "Financial Reports",
      sub: "P&L by trade · Balance Sheet · Cash Flow",
      metric: "Open hub",
    },
  ];

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">General Ledger</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Book of record. Every PO, bill, invoice, payroll run, and job close posts here.
        </p>
      </div>

      <CockpitSection>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {tiles.map(t => (
            <Link
              key={t.to}
              to={t.to}
              className="rounded-md border border-border bg-card p-4 hover:bg-accent/40 transition-colors flex items-start gap-3"
            >
              <div className="h-9 w-9 rounded-sm bg-muted flex items-center justify-center shrink-0">
                <t.icon className="h-4 w-4 text-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium">{t.title}</div>
                  <div className="text-xs text-muted-foreground tabular-nums">{t.metric}</div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t.sub}</p>
              </div>
            </Link>
          ))}
        </div>
      </CockpitSection>

      <CockpitSection>
        <div className="rounded-md border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
            All-time
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">Total Debits</div>
              <div className="font-semibold tabular-nums">{fmt(totalDebits)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Total Credits</div>
              <div className="font-semibold tabular-nums">{fmt(totalCredits)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Status</div>
              <div className={`font-semibold ${inBalance ? "text-foreground" : "text-destructive"}`}>
                {inBalance ? "Balanced" : "Out of balance"}
              </div>
            </div>
          </div>
        </div>
      </CockpitSection>
    </CockpitPage>
  );
}
